import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import { FetchPolicy } from '@apollo/client';
import { generateImagePath, ImagePreset, Playable, useContent } from '@vodafoneis/sjonvarpskjarni-js-lib';
import ContentInfo from '../../../components/ContentInfo/ContentInfo';
import { breakpoints } from '../../../styles/breakpoints';
import { SimilarContent } from '../../../components/SimilarContent';
import { SIMILAR_CONTENT_LIMIT, ACTORS_LIMIT } from '../../../config/constants';
import { ContentButtons } from '../../../components/ContentButtons/ContentButtons';
import { processImageUrl } from '../../../components/ImageComponent';
import { client } from '../../../api/ApolloClient';
import { MetaTags } from '../../../components/MetaTags';
import { ContentSsr } from '../../../@types/ContentSsr';
import CONTENT_SIMPLE from '../../../graphql/ContentSimple.graphql';
import { HeaderGap } from '../../../components/HeaderGap/HeaderGap.styles';
import { TempSpacerForOldDesign } from '../../../components/Divider';

const MovieSceneContainer = styled.div`
	min-height: 100vh;
	display: flex;

	@media ${breakpoints.sm} {
		display: block;
	}
`;

const queryOptions = {
	fetchPolicy: 'network-only' as FetchPolicy,
	nextFetchPolicy: 'cache-first' as FetchPolicy,
};

const MovieSceneContent: React.FC<{ movieId: number }> = ({ movieId }) => {
	const contentProps = useContent(movieId, SIMILAR_CONTENT_LIMIT, ACTORS_LIMIT, queryOptions);
	return (
		<MovieSceneContainer>
			<ContentInfo {...contentProps}>
				<ContentButtons content={contentProps?.content} playable={contentProps?.content as Playable} />
				<TempSpacerForOldDesign />
				{/* TempSpacerForOldDesign is needed to push down the content below the buttons, when move this page to the new design this can be removed */}
				<SimilarContent {...contentProps} />
			</ContentInfo>
		</MovieSceneContainer>
	);
};

const MovieScene: React.FC<{ contentSsr?: ContentSsr | null; resolvedUrl?: string }> = ({ contentSsr, resolvedUrl }) => {
	const router = useRouter();
	const { movieId } = router.query;
	const content = useMemo(() => {
		if (!movieId) return null;

		return <MovieSceneContent movieId={Number(movieId)} />;
	}, [movieId]);

	const socialImageUrl = useMemo(() => {
		if (!contentSsr?.promoImage?.hash && !contentSsr?.backdropImage?.hash) return null;

		return processImageUrl(generateImagePath({ hash: contentSsr?.promoImage?.hash ?? contentSsr?.backdropImage?.hash, presets: [ImagePreset.BACKDROP] }));
	}, [contentSsr]);

	if (typeof window !== 'undefined' && contentSsr?.id && contentSsr?.contentRating) {
		(window as any).__CONTENT_RATING_CACHE__ = (window as any).__CONTENT_RATING_CACHE__ || {};
		(window as any).__CONTENT_RATING_CACHE__[contentSsr.id] = contentSsr.contentRating;
	}

	return (
		<HeaderGap>
			<MetaTags title={contentSsr?.title} description={contentSsr?.description} socialImageUrl={socialImageUrl} type={'video.movie'} url={resolvedUrl} />
			{content}
		</HeaderGap>
	);
};

export default MovieScene;

export const getServerSideProps = async ({ params, resolvedUrl }) => {
	const { movieId } = params;

	const { data } = await client.query({
		query: CONTENT_SIMPLE,
		variables: {
			contentId: movieId,
		},
	});

	return {
		props: {
			contentSsr: data.content,
			resolvedUrl,
		},
	};
};
